import {queryOptions} from '@github-ui/react-query'
import {ProjectItemSectionFieldListServerQuery} from '@github-ui/server-persisted-queries'
import type {ProjectItemSectionFieldListServerQueryQuery} from '@github-ui/server-persisted-queries/types/ProjectItemSectionFieldListServerQuery'
import {reactFetchJSON} from '@github-ui/verified-fetch'

type ProjectItemSectionFieldListServerQueryNode = Extract<
  NonNullable<ProjectItemSectionFieldListServerQueryQuery['node']>,
  {__typename: 'ProjectV2Item'}
>
export type ProjectItemSectionFieldListNode = Pick<
  ProjectItemSectionFieldListServerQueryNode,
  'id' | 'isArchived' | 'project' | 'fieldValues'
>

export function projectItemSectionFieldListQueryKey(
  owner: string,
  repo: string,
  issueNumber: number,
  projectItemId: string,
) {
  return ['issue-metadata', 'project-item-section-field-list', {owner, repo, issueNumber, projectItemId}] as const
}

export function projectItemSectionFieldListQueryOptions(
  owner: string,
  repo: string,
  issueNumber: number,
  projectItemId: string,
) {
  return queryOptions({
    queryKey: projectItemSectionFieldListQueryKey(owner, repo, issueNumber, projectItemId),
    queryFn: ({signal}) => fetchProjectItemSectionFieldList(owner, repo, issueNumber, projectItemId, signal),
  })
}

export async function fetchProjectItemSectionFieldList(
  owner: string,
  repo: string,
  issueNumber: number,
  projectItemId: string,
  signal?: AbortSignal,
): Promise<ProjectItemSectionFieldListNode> {
  const params = new URLSearchParams({
    project_item_id: projectItemId,
    queryId: ProjectItemSectionFieldListServerQuery,
  })
  const response = await reactFetchJSON(
    `/${owner}/${repo}/issues/${issueNumber}/project_item_section_field_list?${params.toString()}`,
    {signal},
  )

  if (!response.ok) throw new Error(`Project item field list fetch failed (${response.status})`)

  const json = (await response.json()) as {
    data?: ProjectItemSectionFieldListServerQueryQuery
    errors?: unknown[]
  }
  if (json.errors?.length || !json.data) throw new Error('Project item field list query returned errors')

  const node = json.data.node
  if (!node || !('project' in node)) {
    throw new Error('Project item field list query returned no project item')
  }

  return {
    id: node.id,
    isArchived: node.isArchived,
    project: node.project,
    fieldValues: node.fieldValues,
  }
}
